/**
 * /field/pt/jobs/:id — one pressure-test job on the phone (PT-3): the header
 * card, the test checklist, Add test, and Complete job.
 *
 * READS `GET /pressure-testing/jobs/:id`. A field worker reading another
 * technician's job gets 403 `not_assigned`; that is a refusal and is shown as
 * one, never as an empty job.
 *
 * COMPLETE IS NEVER PRE-CHECKED. The button shows on an active job and the API
 * decides: a pending test is refused with `tests_incomplete` and the list in
 * `pending_tests`, drawn here as sent, each linking to its test. A scheduled
 * job cannot be started from the field — that is an office transition
 * (PT_DISPATCH) — so the screen says so instead of offering a button the API
 * would refuse.
 *
 * SIGNATURES (PT-4): the technician's and the witness's, captured here until
 * the certificate is issued (PtFieldSignatures.tsx).
 *
 * FIELD TICKETS (PT-5): the job's tickets, each opening its review and the
 * company man's signature, and "Close out today" on a per-day job
 * (PtFieldTickets.tsx).
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, ChevronRight, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompletePtJob, usePtJob } from '../../../hooks/usePressureTesting';
import { formatCalendarDate } from '../../../lib/calendar-date';
import { queryFailureError } from '../../../lib/load-failure';
import { canWritePtTests, formatPressure, ptJobStatusSkin } from '../../../lib/pressure-testing';
import type { PtJobDetail, PtPressureUnit, PtTest } from '../../../types/pressure-testing';
import { ptWriteError, type PtWriteError } from '../PtJobDialogs';
import { ptJobWhere, ptTestsDone } from './PtMyJobsScreen';
import {
  PT_FIELD_PRIMARY,
  PT_FIELD_SECONDARY,
  PtFieldBadge,
  PtFieldCard,
  PtFieldEmpty,
  PtFieldPage,
  PtFieldQueryState,
  PtFieldSectionTitle,
  PtRefusal,
  ptTestStateSkin,
} from './PtFieldBits';
import { PtTestSetupForm } from './PtTestSetupForm';
import { PtFieldSignatures } from './PtFieldSignatures';
import { PtFieldJobTickets } from './PtFieldTickets';

export const ptTestPath = (jobId: string, testId: string) => `/field/pt/tests/${testId}?job=${encodeURIComponent(jobId)}`;

function TestRow({ job, test, n, unit }: { job: PtJobDetail; test: PtTest; n: number; unit: PtPressureUnit | null }) {
  const skin = ptTestStateSkin(test);
  return (
    <Link to={ptTestPath(job.id, test.id)} className="block" data-testid="pt-field-test">
      <PtFieldCard className={`flex min-h-[72px] items-center gap-3 border-l-4 px-4 py-3 active:bg-fieldiq-dark/50 ${skin.rail}`}>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-bold text-white">
              {n}. {test.test_type_name || '—'}
            </span>
            <PtFieldBadge skin={skin} />
          </div>
          <p className="mt-1 text-sm text-fieldiq-text">
            {formatPressure(test.test_pressure, unit)}
            {test.hold_minutes == null ? ' · hold —' : ` · ${test.hold_minutes} min hold`}
          </p>
        </div>
        <ChevronRight className="size-6 shrink-0 text-fieldiq-muted" aria-hidden="true" />
      </PtFieldCard>
    </Link>
  );
}

export default function PtFieldJobScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const query = usePtJob(id);
  const complete = useCompletePtJob();
  const [adding, setAdding] = useState(false);
  const [refusal, setRefusal] = useState<PtWriteError | null>(null);

  const job = query.data?.data ?? null;
  const unit = query.data?.meta.pressure_unit ?? null;
  const editable = job?.status === 'scheduled' || job?.status === 'active';
  const canTests = editable && canWritePtTests(user, job) && !!unit;
  const notFound = (queryFailureError(query) as { response?: { status?: number } } | null)?.response?.status === 404;

  // A refusal describes the job as it was; a fresh read replaces it.
  useEffect(() => setRefusal(null), [query.dataUpdatedAt]);

  const onComplete = () => {
    if (!job) return;
    setRefusal(null);
    complete.mutate(
      { id: job.id },
      {
        onSuccess: () => toast.success(`${job.job_number} complete`),
        onError: (err) => setRefusal(ptWriteError(err, 'The job was not completed.')),
      },
    );
  };

  return (
    <PtFieldPage title={job?.job_number || 'Test job'} backTo="/field/pt" testId="pt-field-job">
      {notFound ? (
        <PtFieldEmpty>This test job was not found.</PtFieldEmpty>
      ) : (
        <PtFieldQueryState query={query} subject="this test job">
          {job && (
            <>
              <PtFieldCard className="px-4 py-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-lg font-extrabold text-fieldiq-text">{job.job_number || '—'}</span>
                  <PtFieldBadge skin={ptJobStatusSkin(job.status)} testId="pt-field-job-status" />
                </div>
                <p className="mt-1 text-lg font-bold text-white">{job.customer_name || '—'}</p>
                <p className="mt-0.5 text-sm text-fieldiq-text">{ptJobWhere(job)}</p>
                <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Scheduled</dt>
                    <dd className="text-fieldiq-text">{formatCalendarDate(job.scheduled_date) ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Tests</dt>
                    <dd className="text-fieldiq-text">{ptTestsDone(job.test_counts)}</dd>
                  </div>
                </dl>
                <div className="mt-3">
                  <p className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Scope</p>
                  <p className="mt-0.5 whitespace-pre-wrap text-sm text-fieldiq-text">{job.scope || '—'}</p>
                </div>
              </PtFieldCard>

              {job.status === 'scheduled' && (
                <p className="mt-3 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text">
                  This job is scheduled. The office starts it; you can set up its tests now.
                </p>
              )}

              <PtFieldSectionTitle>Tests</PtFieldSectionTitle>
              {job.tests.length === 0 ? (
                <PtFieldEmpty>No tests are recorded on this job yet.</PtFieldEmpty>
              ) : (
                <div className="space-y-2" data-testid="pt-field-checklist">
                  {job.tests.map((t, i) => (
                    <TestRow key={t.id} job={job} test={t} n={i + 1} unit={unit} />
                  ))}
                </div>
              )}

              {canTests && !adding && (
                <button type="button" className={`${PT_FIELD_SECONDARY} mt-3`} onClick={() => setAdding(true)} data-testid="pt-field-add-test">
                  <Plus className="size-5" aria-hidden="true" />
                  Add test
                </button>
              )}
              {canTests && adding && (
                <PtFieldCard className="mt-3 px-4 py-4">
                  <h2 className="mb-3 text-base font-bold text-white">Add test</h2>
                  <PtTestSetupForm
                    jobId={job.id}
                    test={null}
                    unit={unit}
                    onCancel={() => setAdding(false)}
                    onSaved={(saved) => {
                      setAdding(false);
                      toast.success('Test added');
                      if (saved) navigate(ptTestPath(job.id, saved.id));
                    }}
                  />
                </PtFieldCard>
              )}
              {editable && !unit && (
                <p className="mt-3 text-sm text-fieldiq-text">The tenant's pressure unit did not arrive, so tests cannot be recorded here.</p>
              )}

              {job.status === 'active' && canWritePtTests(user, job) && (
                <div className="mt-6 space-y-3">
                  <PtRefusal message={refusal?.message} testId="pt-field-complete-refusal">
                    {refusal?.pendingTests && refusal.pendingTests.length > 0 && (
                      <ul className="mt-2 space-y-1" data-testid="pt-field-pending-tests">
                        {refusal.pendingTests.map((t) => (
                          <li key={t.id}>
                            <Link to={ptTestPath(job.id, t.id)} className="font-bold underline">
                              {t.test_type_name} #{t.number}
                            </Link>{' '}
                            — no result recorded
                          </li>
                        ))}
                      </ul>
                    )}
                  </PtRefusal>
                  <button type="button" className={PT_FIELD_PRIMARY} onClick={onComplete} disabled={complete.isPending} data-testid="pt-field-complete">
                    {complete.isPending ? <Loader2 className="size-5 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-5" aria-hidden="true" />}
                    Complete job
                  </button>
                </div>
              )}
              {job.status === 'complete' && (
                <p className="mt-6 rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text">
                  This job is complete. Its tests can no longer be changed.
                </p>
              )}
              {job.status === 'cancelled' && (
                <p className="mt-6 rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-steel bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text">
                  This job was cancelled. {job.cancel_reason || ''}
                </p>
              )}

              <PtFieldJobTickets job={job} today={query.data?.meta.today ?? null} />

              {canWritePtTests(user, job) && <PtFieldSignatures job={job} timeZone={query.data?.meta.time_zone ?? null} />}
            </>
          )}
        </PtFieldQueryState>
      )}
    </PtFieldPage>
  );
}
